import { useState } from 'react';
import { connect } from 'react-redux';
import i18n from 'src/utils/translate';
import styled from 'styled-components';
import config from 'config/config';

import { showSignupModal, showLoginModal } from 'src/modules/shared/modal/actions';
import signupContext from 'src/constants/signupContext';
import { trackFootfallEvent, trackFootfallEventWithHrefDelay } from 'src/utils/footfall';

import Button from 'src/components/buttons/button';
import SearchBar from './searchBar';
import MobileSearchInput from './mobileSearchInput';
import { textLinkSecondary } from 'styles-js/mixins/typography';
import { LinkButton } from 'styles-js/buttons';
import EventType from 'src/constants/footfallEventTypes';

const SiteBar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 50px;
  line-height: 50px;
  background-color: ${props => props.theme.colorWhite};
  z-index: ${props => props.theme.zindexLiftTop};
  border-bottom: ${props => props.theme.greyLine};
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

const SiteBarInner = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (${props => props.theme.underScreenSmall}) {
    padding: 0 15px;
  }
`;

const SitebarActionsLeft = styled.div`
  float: left;
  display: flex;
  align-items: center;
  height: 50px;
`;

const SitebarActionsRight = styled.div`
  float: right;
`;

const LogoContainer = styled.div`
  margin-right: 6px;

  a {
    display: flex;
  }

  @media (${props => props.theme.overScreenSmall}) {
    margin-right: 2px;
  }
`;

const DesktopLogo = styled.img`
  margin-left: -14px;
`;

const MobileLogo = styled.img`
  margin-left: -8px;

  @media (${props => props.theme.overScreenSmall}) {
    display: none;
  }
`;

const MobileSearchButton = styled(LinkButton)`
  min-width: 110px;
  padding: 0 10px;
  line-height: 35px;
  border-radius: 4px;
  background-color: ${props => props.theme.colorBlueBackground};
  color: ${props => props.theme.colorTextBlack};
  font-size: ${props => props.theme.fontSizeXs};
  text-align: left;

  @media (${props => props.theme.overScreenXsmall}) {
    display: none;
  }

  &:hover {
    color: ${props => props.theme.colorTextBlack};
  }

  .icon {
    float: right;
    height: 35px;
    font-size: 17px;
    color: ${props => props.theme.colorTextBlack};
  }
`;

const SearchContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 1006;
  background-color: ${props => props.theme.colorWhite};
`;

const Link = styled.a`
  ${textLinkSecondary};
  margin-left: 15px;
`;

const JoinButton = styled(Button)`
  margin: -4px 0 0 15px;
`;

type Props = {
  showSignupModal: (context: { section: string, element: string }) => void,
  showLoginModal: () => void,
};

const VisitorSiteBar = ({showSignupModal, showLoginModal }: Props) => {
  const [isSearching, setIsSearching] = useState(false);

  return (
    <SiteBar role="navigation">
      <SiteBarInner>
        <SitebarActionsLeft>
          <LogoContainer itemScope={true} itemType="https://schema.org/Organization">
            <a
              href="/"
              itemProp="url"
              onClick={() => {
                trackFootfallEventWithHrefDelay('/', EventType.CLICKED, {
                  clickMetadata: { section: 'hu-logo' },
                  clickType: 'visitor-sitebar',
                });
              }}
            >
              <DesktopLogo
                alt="HealthUnlocked"
                className="hidden-xs"
                height="54"
                itemProp="logo"
                src={`${config.cdn.static}img/logos/healthunlocked-logo.svg`}
                width="214"
              />
              <MobileLogo
                alt="HealthUnlocked"
                height="50"
                src={`${config.cdn.static}img/logos/H-logo-speech-bubble-green.svg`}
                width="50"
              />
            </a>
          </LogoContainer>

          <MobileSearchButton
            aria-label="Start a search"
            onClick={() => setIsSearching(true)}
          >
            {i18n.t('Search')}
            <span className="icon icon-search-1" />
          </MobileSearchButton>

          <SearchBar
            className="search-bar--navbar hidden-xxs"
            customId="-visitor"
            onClick={() => {
              trackFootfallEvent(EventType.CLICKED, {
                clickMetadata: { section: 'search-bar' },
                clickType: 'visitor-sitebar',
              });
            }}
          />
        </SitebarActionsLeft>

        <SitebarActionsRight>
          <Link
            className="hidden-xs"
            href="https://about.healthunlocked.com"
            onClick={() => {
              trackFootfallEventWithHrefDelay('https://about.healthunlocked.com', EventType.CLICKED, {
                clickMetadata: { section: 'about-us' },
                clickType: 'visitor-sitebar',
              });
            }}
          >
            {i18n.t('About')}
          </Link>

          <Link
            data-test-id="visitor-sitebar-login-button"
            href="/login"
            id="sitebar-login-button"
            onClick={event => {
              event.preventDefault();
              trackFootfallEvent(EventType.CLICKED, {
                clickMetadata: { section: 'login' },
                clickType: 'visitor-sitebar',
              });
              showLoginModal();
            }}
          >
            {i18n.t('Log in')}
          </Link>

          <JoinButton
            dataTestId="visitor-sitebar-signup-button"
            id="sitebar-signup-button"
            text={i18n.t('Join')}
            onClick={() => {
              trackFootfallEvent(EventType.CLICKED, {
                clickMetadata: { section: 'join' },
                clickType: 'visitor-sitebar',
              });
              showSignupModal({
                section: signupContext.section.HEADER,
                element: signupContext.element.SIGNUP,
              });
            }}
          />
        </SitebarActionsRight>
      </SiteBarInner>

      {isSearching && (
        <SearchContainer>
          <MobileSearchInput onCancel={() => setIsSearching(false)} />
        </SearchContainer>
      )}
    </SiteBar>
  );
};

export default connect(null, { showLoginModal, showSignupModal })(VisitorSiteBar);
